import { useState } from 'react';
import { useRequest } from 'ahooks';

import {
  ButtonWithArrowRight,
  Footer,
  HoldingPhone,
  InputFileButton,
  TransshipmentSendFilePreview,
  LoadingSpinner,
  Text,
  Toast,
  Button,
} from '~components';
import NumberedList, { NumberedListItem } from '~components/dumbs/NumberedList';
import { useTransshipment } from '~contexts/TranshipmentContext';
import { Response } from '~types/getUserData';
import { Representative, Spouse } from '~services';
import { formatFileToBase64 } from '~utils/formatters';

import * as amplitude from '@amplitude/analytics-browser';
import * as Styled from './FrontTutorialUploadFile.styled';

type Param = [string, { file: string; type: string }];

type TPageMode =
  | 'INITIAL'
  | 'FILESELECTED'
  | 'SENDING'
  | 'DONESENDING'
  | 'BACKENDERROR';

const fileMaxSizeKB = process.env.REACT_APP_MAX_FILE_SIZE || 4507904;

function IdentityFront({
  onClickOpenCamera,
}: {
  onClickOpenCamera: () => void;
}) {
  const transshipment = useTransshipment();
  const [file, setFile] = useState<File>();
  const [pageMode, setPageMode] = useState<TPageMode>('INITIAL');

  const [isToastVisible, setIsToastVisible] = useState(false);
  const [toastErrorMessage, setToastMessage] = useState<string>();

  const request =
    transshipment.type === 'spouse'
      ? Spouse.addDocument
      : Representative.addDocument;

  const { runAsync: addDocument } = useRequest<Response, Param>(request, {
    manual: true,
    onError: () => setPageMode('BACKENDERROR'),
    onSuccess: () => {
      if (transshipment.type === 'representative') {
        amplitude.track({
          event_type: 'documentation_id_sent',
          event_properties: {
            CPF_representatives: transshipment.data.cpf,
          },
        });
      }

      setPageMode('DONESENDING');
    },
  });

  async function sendFile() {
    setPageMode('SENDING');

    const base64File = await formatFileToBase64(file);

    await addDocument(transshipment?.id, {
      file: base64File,
      type: 'rg',
    });
  }

  function handleContinue() {
    transshipment.nextStep();
  }

  function handleFileChange(newFile: File | undefined) {
    if (!newFile) return setPageMode('INITIAL');

    if (!/\.pdf$/.test(newFile.name)) {
      setToastMessage('O arquivo deve ser um PDF');
      setIsToastVisible(true);
      return;
    }

    if (newFile.size > fileMaxSizeKB) {
      setToastMessage(`Arquivo ultrapassa o limite de ${getFileMaxSizeMB()}MB`);
      setIsToastVisible(true);
      return;
    }

    setFile(newFile);
    setPageMode('FILESELECTED');
  }

  function handleClickTakePhoto() {
    onClickOpenCamera();
  }

  function getFileMaxSizeMB() {
    return (fileMaxSizeKB / 1024 / 1024).toFixed(1);
  }

  return (
    <Styled.Container>
      {pageMode === 'INITIAL' && (
        <>
          <Styled.Block>
            <HoldingPhone />
            <Styled.Title>Frente do RG</Styled.Title>

            <NumberedList>
              <NumberedListItem>
                Retire o plástico do seu documento
              </NumberedListItem>
              <NumberedListItem>
                Tire as fotos em um local iluminado
              </NumberedListItem>
              <NumberedListItem>
                Certifique-se que os dados estão legíveis na foto
              </NumberedListItem>
            </NumberedList>

            <Styled.LegendText>
              Caso possua, opte por anexar o PDF do seu RG com até{' '}
              {getFileMaxSizeMB()}MB.
            </Styled.LegendText>
          </Styled.Block>

          <Footer
            marginTop="auto"
            leftButton={
              <InputFileButton
                onChange={(file) => handleFileChange(file)}
                accept=".pdf"
              >
                Anexar PDF
              </InputFileButton>
            }
            rightButton={
              <ButtonWithArrowRight
                width="100%"
                onClick={handleClickTakePhoto}
                variant="primary"
              >
                Tirar foto
              </ButtonWithArrowRight>
            }
          />
        </>
      )}

      {pageMode === 'FILESELECTED' && (
        <>
          <TransshipmentSendFilePreview
            variant="FILESELECTED"
            filename={file.name}
            message={<Text variant="sub400">O PDF do RG está correto?</Text>}
          />

          <Footer
            marginTop="auto"
            leftButton={
              <Button
                width="100%"
                variant="secondary"
                onClick={() => setPageMode('INITIAL')}
              >
                Não, trocar
              </Button>
            }
            rightButton={
              <ButtonWithArrowRight
                width="100%"
                variant="primary"
                onClick={sendFile}
              >
                Sim
              </ButtonWithArrowRight>
            }
          />
        </>
      )}

      {pageMode === 'SENDING' && (
        <>
          <TransshipmentSendFilePreview
            variant="SENDING"
            filename={file.name}
            message={<Text variant="sub400">Enviando PDF do RG</Text>}
          />
          <Styled.EmptyFooter>
            <LoadingSpinner />
          </Styled.EmptyFooter>
        </>
      )}

      {pageMode === 'DONESENDING' && (
        <>
          <TransshipmentSendFilePreview
            variant="DONESENDING"
            filename={file.name}
            message={
              <>
                <Text variant="sub400" color="neutral-strong">
                  <Styled.CheckIcon /> Pdf do RG enviado
                </Text>
              </>
            }
          />
          <Footer
            marginTop="auto"
            rightButton={
              <ButtonWithArrowRight
                width="100%"
                variant="primary"
                onClick={() => handleContinue()}
              >
                Continuar
              </ButtonWithArrowRight>
            }
          />
        </>
      )}

      {pageMode === 'BACKENDERROR' && (
        <>
          <TransshipmentSendFilePreview
            variant="BACKENDERROR"
            filename={file.name}
            message="Erro ao enviar o arquivo, envie novamente."
          />
          <Footer
            marginTop="auto"
            rightButton={
              <ButtonWithArrowRight
                width="100%"
                variant="primary"
                onClick={() => setPageMode('INITIAL')}
              >
                Reenviar
              </ButtonWithArrowRight>
            }
          />
        </>
      )}

      <Toast
        isVisible={!!isToastVisible}
        title={toastErrorMessage}
        onDismiss={() => setIsToastVisible(false)}
        variant="error"
      />
    </Styled.Container>
  );
}

export default IdentityFront;
